// Disclosure popover for the app shell menus (workspaces, agent dock, account). Closes on Escape,
// outside click and route change; focus returns to the trigger.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { cx } from './cx';

export function Popover({
  label,
  trigger,
  triggerClassName,
  align = 'end',
  width = 'w-80',
  children,
}: {
  /** Accessible name of the trigger button. */
  label: string;
  trigger: ReactNode;
  triggerClassName?: string;
  align?: 'start' | 'end';
  width?: string;
  children: (close: () => void) => ReactNode;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const close = () => {
    setOpen(false);
    btn.current?.focus();
  };
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    const onDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);
  return (
    <div ref={root} className="relative">
      <button
        ref={btn}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        onClick={() => setOpen((v) => !v)}
        className={triggerClassName}
      >
        {trigger}
      </button>
      {open ? (
        <div
          id={`${id}-panel`}
          className={cx(
            'fl-popover absolute top-[calc(100%+8px)] z-50 max-h-[calc(100dvh-5rem)] max-w-[calc(100vw-1.5rem)] overflow-y-auto p-2',
            'animate-[fl-rise_var(--t-std)_var(--ease)]',
            align === 'end' ? 'right-0' : 'left-0',
            width,
          )}
        >
          {children(close)}
        </div>
      ) : null}
    </div>
  );
}
